import { useState, useEffect, type ChangeEvent } from "react";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { toast } from "sonner";
import {
  Bell,
  BellOff,
  Trash2,
  Paperclip,
  ChevronDown,
  Sparkles,
  MoreHorizontal,
  Receipt,
  ShieldCheck,
} from "lucide-react";
import DraftInvoiceButton from "@/components/DraftInvoiceButton";
import AttachmentsPanelJs from "@/components/AttachmentsPanel";
// AttachmentsPanel is still JS — the runtime prop-types are inferred as
// required, so we widen at the import site for now.
const AttachmentsPanel = AttachmentsPanelJs as unknown as React.FC<{
  jobId?: string;
  entryId?: string;
  attachments: unknown[];
  onChange: (next: unknown[]) => void;
  onOcrApplied?: (parsedOcr: unknown, filled: string[]) => void;
  testidPrefix?: string;
}>;
import { formatMoney, formatMoneyRounded, formatDateTime } from "@/shared";
import BiSelect from "@/components/BiSelect";

/**
 * <JobCard /> — decluttered single-job tile.
 *
 * Hierarchy (mobile-first):
 *   1. Name + status pill (biggest, right-aligned)
 *   2. Client · address (muted)
 *   3. Progress bar + % badge (visual anchor)
 *   4. Spent / Remaining stat pair (numbers stand out)
 *   5. Chip row (bell · docs · more)
 *   6. Expandable "Manage" strip (threshold · draft · delete · attachments)
 *
 * Everything the previous card exposed still exists — it's just reorganised
 * behind the "More" button so the default state is scannable.
 */

/** Loose Mongo doc shape — we deliberately do NOT tie this to the TS model
 *  yet because the wire format is still snake_case. The next migration step
 *  will move this to an adapter, but that's out of scope for the declutter. */
export interface JobLike {
  id: string;
  name: string;
  client?: string;
  address?: string;
  budget?: number;
  digest_watch?: boolean;
  threshold_pct?: number | null;
  attachments?: unknown[];
}

interface JobCardProps {
  job: JobLike;
  spent: number;
  defaultThreshold: number;
  attachments: unknown[];
  onAttachmentsChange: (next: unknown[]) => void;
  onReload: () => Promise<void>;
  onDelete: (jobId: string) => Promise<void>;
}

const thresholdOptions = [30, 40, 50, 60, 70, 80, 90] as const;

export default function JobCard({
  job,
  spent,
  defaultThreshold,
  attachments,
  onAttachmentsChange,
  onReload,
  onDelete,
}: JobCardProps) {
  const [manageOpen, setManageOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [safetyOpen, setSafetyOpen] = useState(false);
  const [safetyRecords, setSafetyRecords] = useState<Array<{
    id: string;
    title: string;
    items?: Array<{ key: string; checked: boolean }>;
    notes?: string | null;
    created_at?: string;
  }>>([]);
  const [safetyLoaded, setSafetyLoaded] = useState(false);
  const [safetyCount, setSafetyCount] = useState<number>(0);
  const [safetyLatestAt, setSafetyLatestAt] = useState<string | null>(null);

  // Lightweight count fetch — happens on mount, cheap. The full log only
  // loads when the tradie taps the Safety chip to expand.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get<Record<string, { count: number; latest_at: string }>>(
          "/take5/counts"
        );
        if (cancelled) return;
        const meta = data?.[job.id];
        setSafetyCount(meta?.count || 0);
        setSafetyLatestAt(meta?.latest_at || null);
      } catch (err) {
        devLog.debug("take5/counts fetch failed", err);
      }
    })();
    return () => { cancelled = true; };
  }, [job.id]);

  const loadSafety = async () => {
    if (safetyLoaded) return;
    try {
      const { data } = await api.get<typeof safetyRecords>(
        `/take5?job_id=${encodeURIComponent(job.id)}`
      );
      setSafetyRecords(Array.isArray(data) ? data : []);
      setSafetyLoaded(true);
    } catch (err) {
      devLog.warn("take5 list fetch failed", err);
    }
  };

  const budget = Number(job.budget) || 0;
  const remaining = Math.max(0, budget - spent);
  const pct = budget > 0 ? Math.min(999, Math.round((spent / budget) * 100)) : 0;
  const over = budget > 0 && spent > budget;

  // Effective alert threshold — job override wins, else the fleet default.
  const effectiveThreshold = job.threshold_pct ?? defaultThreshold;
  const watched = job.digest_watch !== false;
  const atThreshold = budget > 0 && pct >= effectiveThreshold;

  // Colour tokens driven by state — one source of truth for every accent.
  const statusPill = over
    ? { text: "OVER", cls: "bg-[#F87171]/15 text-[#F87171] border-[#F87171]/30" }
    : atThreshold
    ? { text: `${pct}% USED`, cls: "bg-[#FBBF24]/15 text-[#FBBF24] border-[#FBBF24]/30" }
    : { text: `${pct}% USED`, cls: "bg-white/[0.05] text-white/60 border-white/10" };

  const barColour = over
    ? "bg-[#F87171]"
    : atThreshold
    ? "bg-[#FBBF24]"
    : "bg-[#FF9500]";

  const toggleWatch = async () => {
    try {
      const next = !watched;
      await api.put(`/jobs/${job.id}/digest-watch`, { watch: next });
      await onReload();
      toast(
        next
          ? `${job.name} · included in evening digest`
          : `${job.name} · muted from evening digest`,
        { duration: 3000 }
      );
    } catch (err) {
      devLog.warn("toggle watch failed", err);
      toast.error("Could not update digest setting");
    }
  };

  const changeThreshold = async (e: ChangeEvent<HTMLSelectElement>) => {
    const raw = e.target.value;
    const nextPct = raw === "" ? null : Number(raw);
    try {
      await api.put(`/jobs/${job.id}/threshold`, { threshold_pct: nextPct });
      await onReload();
      toast(
        nextPct === null
          ? `${job.name} · using default alert threshold`
          : `${job.name} · alerts at ${nextPct}% of budget`,
        { duration: 2500 }
      );
    } catch (err) {
      devLog.warn("change threshold failed", err);
      toast.error("Could not update threshold");
    }
  };

  return (
    <div
      data-testid={`job-card-${job.id}`}
      className="bi-surface p-4 space-y-3.5"
    >
      {/* Row 1 — name + status pill */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div
            className="font-heading font-extrabold text-white leading-tight text-[17px] truncate"
            data-testid={`job-name-${job.id}`}
          >
            {job.name}
          </div>
          {(job.client || job.address) && (
            <div className="text-[11px] text-white/45 mt-0.5 truncate">
              {job.client || "—"}
              {job.address ? ` · ${job.address}` : ""}
            </div>
          )}
        </div>
        <span
          data-testid={`job-status-pill-${job.id}`}
          className={`text-[10px] px-2 py-1 rounded-full border font-heading font-bold uppercase tracking-wider whitespace-nowrap shrink-0 ${statusPill.cls}`}
        >
          {statusPill.text}
        </span>
      </div>

      {/* Row 2 — progress bar */}
      {budget > 0 && (
        <div>
          <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${barColour}`}
              style={{ width: `${Math.min(100, pct)}%` }}
            />
          </div>
        </div>
      )}

      {/* Row 3 — spent / remaining stat pair */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/40">
            Spent
          </div>
          <div
            data-testid={`job-spent-${job.id}`}
            className={`font-heading text-lg font-extrabold tabular-nums leading-tight ${over ? "text-[#F87171]" : "text-white"}`}
          >
            {formatMoney(spent)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-white/40">
            {over ? "Over budget" : budget > 0 ? "Remaining" : "Budget"}
          </div>
          <div
            data-testid={`job-remaining-${job.id}`}
            className={`font-heading text-lg font-extrabold tabular-nums leading-tight ${
              over ? "text-[#F87171]" : "text-white/85"
            }`}
          >
            {budget === 0
              ? "No budget"
              : over
              ? formatMoney(spent - budget)
              : formatMoney(remaining)}
          </div>
          {budget > 0 && (
            <div className="text-[10px] text-white/35 mt-0.5 tabular-nums">
              of {formatMoneyRounded(budget)}
            </div>
          )}
        </div>
      </div>

      {/* Row 4 — compact chips */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <button
          type="button"
          onClick={toggleWatch}
          data-testid={`job-digest-watch-${job.id}`}
          title={
            watched
              ? "Included in evening digest — tap to mute"
              : "Muted from evening digest — tap to include"
          }
          className={`h-8 px-2.5 rounded-full text-[11px] font-heading font-bold uppercase tracking-wider inline-flex items-center gap-1.5 transition ${
            watched
              ? "bg-[#FF9500]/15 text-[#FF9500] border border-[#FF9500]/30"
              : "bi-ghost-btn text-white/50"
          }`}
        >
          {watched ? <Bell className="w-3 h-3" /> : <BellOff className="w-3 h-3" />}
          {watched ? `Alert ${effectiveThreshold}%` : "Muted"}
        </button>

        <button
          type="button"
          onClick={() => setDocsOpen((v) => !v)}
          data-testid={`job-toggle-atts-${job.id}`}
          className="h-8 px-2.5 rounded-full text-[11px] bi-ghost-btn inline-flex items-center gap-1.5 text-white/70"
        >
          <Paperclip className="w-3 h-3" />
          {attachments.length > 0 ? `${attachments.length} doc${attachments.length === 1 ? "" : "s"}` : "Docs"}
        </button>

        {/* Safety chip — count of Take 5 dockets against this job. Tapping
            expands a full log of the checks (title, items completed, notes). */}
        <button
          type="button"
          onClick={() => {
            const next = !safetyOpen;
            setSafetyOpen(next);
            if (next) loadSafety();
          }}
          data-testid={`job-safety-${job.id}`}
          aria-expanded={safetyOpen}
          className={`h-8 px-2.5 rounded-full text-[11px] font-heading font-bold uppercase tracking-wider inline-flex items-center gap-1.5 transition ${
            safetyCount > 0
              ? "bg-[#34D399]/12 text-[#34D399] border border-[#34D399]/25"
              : "bi-ghost-btn text-white/60"
          }`}
        >
          <ShieldCheck className="w-3 h-3" />
          {safetyCount > 0 ? `Safety ${safetyCount}` : "Safety"}
        </button>

        {job.threshold_pct != null && (
          <span
            data-testid={`job-threshold-badge-${job.id}`}
            className="text-[10px] px-2 py-0.5 rounded-full bg-[#FF9500]/12 text-[#FF9500] font-heading font-bold uppercase tracking-wider inline-flex items-center gap-1"
          >
            <Sparkles className="w-2.5 h-2.5" />
            Custom
          </span>
        )}

        <div className="flex-1" />

        <button
          type="button"
          onClick={() => setManageOpen((v) => !v)}
          data-testid={`job-more-${job.id}`}
          aria-expanded={manageOpen}
          className={`h-8 px-2.5 rounded-full text-[11px] inline-flex items-center gap-1.5 transition ${
            manageOpen ? "bg-white/[0.08] text-white" : "bi-ghost-btn text-white/70"
          }`}
        >
          <MoreHorizontal className="w-3.5 h-3.5" />
          Manage
        </button>
      </div>

      {/* Row 5 — collapsible "Manage" strip */}
      {manageOpen && (
        <div
          data-testid={`job-manage-${job.id}`}
          className="pt-3 border-t border-white/[0.06] space-y-3"
        >
          {budget > 0 && (
            <div className="flex items-center gap-2">
              <label className="text-[10px] uppercase tracking-wider text-white/40 shrink-0">
                Alert at
              </label>
              <BiSelect
                data-testid={`job-threshold-${job.id}`}
                value={job.threshold_pct == null ? "" : String(job.threshold_pct)}
                onChange={changeThreshold}
                className="bi-input h-9 flex-1 text-xs"
              >
                <option value="">
                  Default ({defaultThreshold}%)
                </option>
                {thresholdOptions.map((v) => (
                  <option key={v} value={String(v)}>
                    {v}% of budget
                  </option>
                ))}
              </BiSelect>
            </div>
          )}

          <div className="flex items-center gap-2">
            <div className="flex-1">
              <DraftInvoiceButton job={job} />
            </div>
            <button
              type="button"
              onClick={() => onDelete(job.id)}
              data-testid={`job-del-${job.id}`}
              title="Delete job"
              className="h-9 px-3 rounded-lg bi-ghost-btn text-red-300/80 hover:text-[#F87171] inline-flex items-center gap-1.5 text-xs"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete
            </button>
          </div>
        </div>
      )}

      {/* Row 6 — attachments accordion (kept separate so it doesn't crowd Manage) */}
      {docsOpen && (
        <div
          data-testid={`job-docs-${job.id}`}
          className="pt-3 border-t border-white/[0.06]"
        >
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2 inline-flex items-center gap-1.5">
            <Receipt className="w-3 h-3" />
            Site documents
          </div>
          <AttachmentsPanel
            jobId={job.id}
            attachments={attachments}
            onChange={onAttachmentsChange}
            testidPrefix={`job-${job.id}`}
          />
        </div>
      )}

      {/* Row 7 — Safety log accordion. Shows every Take 5 docket bound to
          this job (loaded on first expand). */}
      {safetyOpen && (
        <div
          data-testid={`job-safety-log-${job.id}`}
          className="pt-3 border-t border-white/[0.06]"
        >
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2 inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3" />
            Safety log
            {safetyLatestAt && (
              <span className="normal-case tracking-normal text-white/30 ml-1">
                · latest {new Date(safetyLatestAt).toLocaleDateString()}
              </span>
            )}
          </div>
          {!safetyLoaded ? (
            <div className="text-[11px] text-white/40 py-2">Loading…</div>
          ) : safetyRecords.length === 0 ? (
            <div className="text-[11px] text-white/40 py-2" data-testid={`job-safety-empty-${job.id}`}>
              No Take 5 dockets yet — log one from the OHS tab.
            </div>
          ) : (
            <div className="space-y-1.5">
              {safetyRecords.map((r) => {
                const checked = (r.items || []).filter((it) => it.checked).length;
                const total = (r.items || []).length;
                return (
                  <div
                    key={r.id}
                    data-testid={`job-safety-row-${r.id}`}
                    className="flex items-center gap-2 text-[11px] py-1.5 px-2 rounded-md bg-white/[0.03]"
                  >
                    <ShieldCheck className="w-3.5 h-3.5 text-[#34D399] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-white/85 truncate">{r.title}</div>
                      <div className="text-white/40">
                        {checked}/{total} checks
                        {r.created_at ? ` · ${formatDateTime(r.created_at)}` : ""}
                        {r.notes ? " · Notes attached" : ""}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Legacy "site documents" toggle (kept for backwards compat with the test-id
          used by iter34; hidden visually — the new chip above is the user-facing control). */}
      <button
        type="button"
        aria-hidden
        tabIndex={-1}
        onClick={() => setDocsOpen((v) => !v)}
        data-testid={`job-toggle-atts-legacy-${job.id}`}
        className="sr-only"
      >
        <ChevronDown />
      </button>
    </div>
  );
}
